import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const programs = [
  {
    name: 'Union Settlement',
    tag: 'Academic Support',
    description: 'Union Settlement provides enrichment activities, tutoring, and family support services to PS 96 students and their families, helping every child grow and thrive during their extended hours in the building.',
    href: '/union_settlement',
    image: '/assets/images/afterschool/afterschool3.jpg',
    contact: {
      name: 'Anais Rodriguez',
      title: 'Middle School Program Manager',
      phone: '917-215-8999',
      email: 'anrodriguez@unionsettlement.org',
      website: 'https://www.unionsettlement.org',
    },
  },
];

const gallery = [
  '/assets/images/afterschool/afterschool2.jpeg',
  '/assets/images/afterschool/afterschool4.jpg',
];

const AfterSchoolPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Families</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            After School Programs
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            PS 96 partners with Union Settlement to offer safe, enriching after-school care right here in our building.
          </p>
        </div>
      </section>

      {/* Info banner */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 mt-12 mb-12">
        <div className="bg-[#fdbb2e]/10 border border-[#fdbb2e]/30 rounded-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">After School at PS 96</h2>
          <p className="text-gray-700 leading-relaxed">
            Our after-school program runs Monday through Friday after dismissal, offering a fun, safe, and exciting learning environment that supports each child's social and emotional development. Contact our Parent Coordinator for enrollment details and eligibility.
          </p>
          <a href="/contact-us" className="inline-flex items-center gap-2 mt-5 text-sm font-bold text-[#00213a] hover:underline">
            Contact Parent Coordinator <ArrowRightIcon />
          </a>
        </div>
      </section>

      {/* Program cards */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 mb-20">
        <div className="grid gap-8 max-w-3xl mx-auto">
          {programs.map((p) => (
            <div key={p.name} className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#fdbb2e]/40 hover:-translate-y-0.5 transition-all duration-200 flex flex-col">
              <div className="h-56 overflow-hidden border-b border-gray-100">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#00213a] bg-[#fdbb2e]/15 px-3 py-1 rounded-full mb-4 self-start">
                  {p.tag}
                </span>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">{p.name}</h2>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-5">{p.description}</p>
                {p.contact && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-5 text-sm">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#00213a] mb-2">Program Contact</h3>
                    <p className="font-semibold text-gray-900">{p.contact.name}</p>
                    <p className="text-gray-600 mb-3">{p.contact.title}</p>
                    <ul className="space-y-1">
                      <li>
                        <a href={`tel:${p.contact.phone}`} className="text-[#00213a] font-medium hover:underline">{p.contact.phone}</a>
                      </li>
                      <li>
                        <a href={`mailto:${p.contact.email}`} className="text-[#00213a] font-medium hover:underline break-all">{p.contact.email}</a>
                      </li>
                      <li>
                        <a href={p.contact.website} target="_blank" rel="noopener noreferrer" className="text-[#00213a] font-medium hover:underline">
                          www.unionsettlement.org<span className="sr-only"> (opens in a new tab)</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
                <a href={p.href} className="inline-flex items-center gap-2 text-sm font-bold text-[#00213a] group-hover:gap-3 transition-all duration-200">
                  Learn more <ArrowRightIcon />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Photo gallery */}
      <section className="grid grid-cols-1 sm:grid-cols-2">
        {gallery.map((src, i) => (
          <div key={src} className="h-72 sm:h-96 overflow-hidden">
            <img
              src={src}
              alt={`PS 96 after-school program ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Questions About After School?
          </h2>
          <p className="text-xl text-white/80 mb-8">Reach out to our Parent Coordinator for availability, schedules, and enrollment.</p>
          <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-[#fdbb2e] text-[#00213a] font-black rounded-xl hover:bg-[#ffd04e] transition-colors duration-200 shadow-lg">
            Get in Touch <ArrowRightIcon />
          </a>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default AfterSchoolPage;
